import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    MenuItem,
    alpha,
    useTheme,
    IconButton,
    Avatar,
    Snackbar,
    Alert,
    Divider,
    useMediaQuery,
    CircularProgress,
    Select,
    FormControl,
    Menu,
    Tabs,
    Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { withdrawFunds, fetchCryptoWallets, withdrawCryptoFunds, deleteBankAccount } from '../../services/backendAPI';
import { fetchProfileDataAction, updateUserSettingsAndRefreshAction } from '../../store/actions/profile';
import AddBankModal from '../PayIn/AddBankModal';
import AddCryptoWalletModal from '../PayIn/AddCryptoWalletModal';
import FeedbackModal from '../Funds/FeedbackModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { brokerConfig } from '../../config/brokerConfig';

const AccountListItem = ({ 
    id, 
    title, 
    subtitle, 
    icon, 
    isSelected, 
    onSelect, 
    onDelete,
    theme 
}) => (
    <Box
        onClick={() => onSelect(id)}
        sx={{
            py: 0.75,
            px: 1.25,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            borderLeft: isSelected 
                ? `4px solid ${theme.palette.primary.main}` 
                : '4px solid transparent',
            bgcolor: isSelected
                ? alpha(theme.palette.primary.main, 0.04)
                : 'transparent',
            '&:hover': {
                bgcolor: isSelected
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.action.hover, 0.05)
            }
        }}
    >
        <Avatar
            sx={{
                width: 28,
                height: 28,
                bgcolor: isSelected
                    ? alpha(theme.palette.primary.main, 0.2)
                    : alpha(theme.palette.primary.main, 0.1)
            }}
        >
            {React.cloneElement(icon, {
                sx: {
                    fontSize: 16,
                    color: isSelected
                        ? theme.palette.primary.main
                        : alpha(theme.palette.primary.main, 0.7)
                }

            })}
        </Avatar>
        <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2, mb: 0.25 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
                {subtitle}
            </Typography>
        </Box>
        {onDelete && (
            <IconButton 
                size="small" 
                onClick={(e) => {
                    e.stopPropagation();
                    onDelete(e, id);
                }}
                sx={{ mr: 1, color: theme.palette.error.main }}
            >
                <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
        )}
        <Box sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: `2px solid ${isSelected
                ? theme.palette.primary.main
                : theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {isSelected && (
                <Box sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main
                }} />
            )}
        </Box>
    </Box>
);

const WithdrawModal = ({ open, onClose }) => {
    const theme = useTheme();
    const userData = useSelector(state => state.profile.userData);
    const userSettings = useSelector(state => state.profile.userSettings);
    const brokerSettings = useSelector(state => state.profile.brokerSettings);
    const payoutTransactions = useSelector(state => state.funds.payoutTransactions);
    const bankAccounts = userData?.bank_accounts;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeTab, setActiveTab] = useState(0);
    const [cryptoWallets, setCryptoWallets] = useState([]);
    const [isLoadingCrypto, setIsLoadingCrypto] = useState(false);
    const [amount, setAmount] = useState('1000');
    const [selectedBank, setSelectedBank] = useState(bankAccounts?.[0]?.bank_account_number || '');
    const [amountError, setAmountError] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error'
    });
    const [showAddBankModal, setShowAddBankModal] = useState(false);
    const [showAddCryptoWalletModal, setShowAddCryptoWalletModal] = useState(false);
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const [deletingBankId, setDeletingBankId] = useState(null);
    const [bankToDelete, setBankToDelete] = useState(null);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        if (open) {
            setActiveTab(0);
            setAmount('1000');
            if (bankAccounts?.length > 0) {
                setSelectedBank(bankAccounts[0].bank_account_number);
            }
            
            // Fetch crypto wallets
            setIsLoadingCrypto(true);
            fetchCryptoWallets()
                .then(res => {
                    if (res?.status === 'success' && res?.data?.wallets) {
                        setCryptoWallets(res.data.wallets);
                        // Default selection if no bank accounts exist
                        if (!bankAccounts?.length && res.data.wallets.length > 0) {
                            setSelectedBank(res.data.wallets[0].wallet_address);
                            setActiveTab(1);
                        }
                    }
                })
                .catch(err => console.error("Failed to fetch crypto wallets", err))
                .finally(() => setIsLoadingCrypto(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleAmountChange = (event) => {
        const value = event.target.value;
        const cleanValue = value.replace(/[^\d.]/g, '');
        const parts = cleanValue.split('.');
        const formattedValue = parts[0] + (parts.length > 1 ? '.' + parts[1].slice(0, 2) : '');

        if (parseFloat(formattedValue) < 1000) {
            setAmountError('Minimum withdrawal amount is ₹1000');
            // } else if (parseFloat(formattedValue) > 1000000) {
            //     setAmountError('Maximum withdrawal amount is ₹10,00,000');
        } else {
            setAmountError('');
        }

        setAmount(formattedValue);
    };

    const handleWithdraw = async () => {
        setIsWithdrawing(true);
        try {
            const isCryptoWallet = cryptoWallets.some(wallet => wallet.wallet_address === selectedBank);

            if (isCryptoWallet) {
                await withdrawCryptoFunds({
                    amount: Number(amount) * 100,
                    client_id: localStorage.getItem("login_id"),
                    wallet_public_id: cryptoWallets.find(wallet => wallet.wallet_address === selectedBank)?.wallet_public_id
                });
            } else {
                await withdrawFunds({
                    amount: String(Number(amount) * 100),
                    client_id: localStorage.getItem("login_id"),
                    bank_account_number: selectedBank,
                    bank_name: bankAccounts.find(bank => bank.bank_account_number === selectedBank).bank_name,
                    ifsc: bankAccounts.find(bank => bank.bank_account_number === selectedBank).ifsc
                });
            }
            
            setSnackbar({
                open: true,
                message: 'Withdrawal request submitted successfully',
                severity: 'success'
            });
            setTimeout(() => {
                const feedbackUrl = brokerConfig?.feedback?.url;
                const feedbackProvided = userSettings?.feedback_provided;
                const payoutCount = payoutTransactions?.payout_count;

                // Add current withdrawal to payout count logic since it was just successful
                if (!feedbackProvided && feedbackUrl && (payoutCount + 1) >= 3) {
                    onClose();
                    setIsFeedbackModalOpen(true);
                } else {
                    onClose();
                }

                setAmount('');
                setSelectedBank('');
                setIsWithdrawing(false);
            }, 2000);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error || 'Failed to process withdrawal request',
                severity: 'error'
            });
            setIsWithdrawing(false);
        }
    };

    const handleAddBankClose = () => {
        setShowAddBankModal(false);
    };

    const handleAddCryptoWalletClose = () => {
        setShowAddCryptoWalletModal(false);
    };

    const handleSelectBank = () => {
        setShowAddBankModal(true);
    };

    const handleSelectCrypto = () => {
        setShowAddCryptoWalletModal(true);
    };

    const initiateDeleteBank = (e, accountNumber) => {
        e.stopPropagation();
        setBankToDelete(accountNumber);
    };

    const confirmDeleteBank = async () => {
        if (!bankToDelete) return;
        setDeletingBankId(bankToDelete);
        try {
            const bankToDeleteData = bankAccounts?.find(bank => bank.bank_account_number === bankToDelete);
            const response = await deleteBankAccount({
                client_id: localStorage.getItem("login_id"),
                accountType: "bank_inr",
                accountNumber: bankToDelete,
                ifscCode: bankToDeleteData?.ifsc || ""
            });

            if (response?.status === 'success' || response?.data?.status === 'success') {
                setSnackbar({
                    open: true,
                    message: response?.message || 'Bank account deleted successfully',
                    severity: 'success'
                });

                // If we deleted the currently selected bank, clear selection
                if (selectedBank === bankToDelete) {
                    setSelectedBank('');
                }

                // Refresh the profile data to get updated bank list
                dispatch(fetchProfileDataAction());
            } else {
                throw new Error(response?.message || 'Failed to delete bank account');
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.message || error || 'Failed to delete bank account',
                severity: 'error'
            });
        } finally {
            setDeletingBankId(null);
            setBankToDelete(null);
        }
    };

    const isSelectedBankValid = () => {
        if (activeTab === 0) {
            return bankAccounts?.some(bank => bank.bank_account_number === selectedBank);
        } else {
            return cryptoWallets?.some(wallet => wallet.wallet_address === selectedBank);
        }
    };

    const isValidForm = amount && !amountError && selectedBank && isSelectedBankValid();

    return (
        <>
            {!showAddBankModal && !showAddCryptoWalletModal ? (
                <Dialog
                    open={open}
                    onClose={onClose}
                    maxWidth="xs"
                    fullWidth
                    fullScreen={isMobile}
                    PaperProps={{
                        sx: {
                            borderRadius: isMobile ? 0 : 3,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                            overflow: 'hidden',
                            maxWidth: isMobile ? '100%' : '400px !important',
                            height: isMobile ? '100%' : 'auto',
                            display: 'flex',
                            flexDirection: 'column'
                        }
                    }}
                >
                    <Box sx={{
                        background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        p: isMobile ? 1.5 : 2,
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            opacity: 0.1,
                            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                            backgroundSize: '24px 24px',
                        }} />
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            pt: isMobile ? 'env(safe-area-inset-top)' : 0
                        }}>
                            <Box>
                                <Typography variant="h6" sx={{
                                    color: 'white',
                                    fontWeight: 700,
                                    mb: 0.25,
                                    fontSize: isMobile ? '1.125rem' : '1.25rem'
                                }}>
                                    Withdraw Funds
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    Transfer money to your bank account
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={onClose}
                                size="small"
                                sx={{
                                    color: 'white',
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>

                    <DialogContent sx={{
                        p: isMobile ? 1.5 : 2,
                        flex: isMobile ? 1 : 'initial',
                        overflow: isMobile ? 'hidden' : 'auto',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Box sx={{
                            mb: 1.5,
                            p: 1,
                            bgcolor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.primary.main, 0.05)
                                : alpha(theme.palette.primary.light, 0.05),
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }}>
                            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                    Amount to Withdraw
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    Min: ₹1000
                                </Typography>
                            </Box>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="0.00"
                                value={amount}
                                onChange={handleAmountChange}
                                error={Boolean(amountError)}
                                helperText={amountError}
                                InputProps={{
                                    startAdornment: (
                                        <Typography sx={{
                                            fontSize: '1rem',
                                            color: theme.palette.text.primary,
                                            mr: 1,
                                            fontWeight: 500
                                        }}>₹</Typography>
                                    )
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: theme.palette.background.paper,
                                    },
                                    '& input': {
                                        fontSize: '1.125rem',
                                        fontWeight: 600,
                                        textAlign: 'center',
                                        py: 0.5,
                                    }
                                }}
                            />
                        </Box>
                        
                        <Box sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            overflow: 'hidden',
                            bgcolor: theme.palette.background.paper,
                            display: 'flex',
                            flexDirection: 'column',
                            flex: isMobile ? 1 : 'initial'
                        }}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs 
                                    value={activeTab} 
                                    onChange={(e, val) => setActiveTab(val)}
                                    variant="fullWidth"
                                    sx={{
                                        minHeight: 40,
                                        '& .MuiTab-root': {
                                            minHeight: 40,
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            textTransform: 'none'
                                        }
                                    }}
                                >
                                    <Tab value={0} label="Bank Accounts" />
                                    <Tab value={1} label="Crypto Wallets" />
                                </Tabs>
                            </Box>


                            <Box sx={{ 
                                flex: 1, 
                                overflowY: 'auto', 
                                maxHeight: isMobile ? 'none' : '40vh' 
                            }}>
                                <Box sx={{ display: (activeTab === 0 || activeTab === '0') ? 'block' : 'none' }}>
                                    {bankAccounts?.length > 0 ? bankAccounts.map((bank, index) => (
                                        <Box key={bank.bank_account_number}>
                                            <AccountListItem
                                                id={bank.bank_account_number}
                                                title={bank.bank_name}
                                                subtitle={`A/C: ${bank.bank_account_number}`}
                                                icon={<AccountBalanceIcon />}
                                                isSelected={selectedBank === bank.bank_account_number}
                                                onSelect={setSelectedBank}
                                                onDelete={brokerSettings?.enableBankAddition === 'true' ? initiateDeleteBank : null}
                                                theme={theme}
                                            />
                                            {index < bankAccounts.length - 1 && <Divider />}
                                        </Box>
                                    )) : (
                                        <Typography variant="body2" sx={{ p: 4, color: 'text.secondary', textAlign: 'center' }}>
                                            No bank accounts added yet
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ display: (activeTab === 1 || activeTab === '1') ? 'block' : 'none' }}>
                                    {isLoadingCrypto ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : cryptoWallets?.length > 0 ? cryptoWallets.map((wallet, index) => (
                                        <Box key={wallet.wallet_address}>
                                            <AccountListItem
                                                id={wallet.wallet_address}
                                                title={wallet.wallet_label || `${wallet.currency} Wallet`}
                                                subtitle={`${wallet.network} • Add: ${wallet.wallet_address.substring(0,6)}...${wallet.wallet_address.substring(wallet.wallet_address.length - 4)}`}
                                                icon={<AccountBalanceWalletIcon />}
                                                isSelected={selectedBank === wallet.wallet_address}
                                                onSelect={setSelectedBank}
                                                theme={theme}
                                            />
                                            {index < cryptoWallets.length - 1 && <Divider />}
                                        </Box>
                                    )) : (
                                        <Typography variant="body2" sx={{ p: 4, color: 'text.secondary', textAlign: 'center' }}>
                                            No crypto wallets added yet
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{
                        p: isMobile ? 1.5 : 2,
                        bgcolor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.paper, 0.5)
                            : alpha(theme.palette.grey[50], 0.5),
                        borderTop: `1px solid ${theme.palette.divider}`,
                        pb: isMobile ? 'calc(env(safe-area-inset-bottom) + 12px)' : 2
                    }}>
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: 1,
                            justifyContent: 'flex-end'
                        }}>
                            <Button
                                onClick={(activeTab === 0 || activeTab === '0') ? handleSelectBank : handleSelectCrypto}
                                size={isMobile ? "medium" : "small"}
                                fullWidth={isMobile}
                                variant="outlined"
                                startIcon={<AddIcon />}
                                sx={{
                                    order: isMobile ? 2 : 1
                                }}
                            >
                                {(activeTab === 0 || activeTab === '0') ? 'Add New Bank Account' : 'Add New Crypto Wallet'}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleWithdraw}
                                disabled={!isValidForm || isWithdrawing}
                                endIcon={<ArrowForwardIcon />}
                                size={isMobile ? "medium" : "small"}
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: 1.5,
                                    px: 2,
                                    bgcolor: theme.palette.success.main,
                                    '&:hover': {
                                        bgcolor: theme.palette.success.dark
                                    },
                                    order: isMobile ? 1 : 2
                                }}
                            >
                                {isWithdrawing ? 'Processing...' : 'Withdraw'}
                            </Button>
                        </Box>
                    </DialogActions>
                </Dialog>
            ) : null}
            
            {showAddBankModal && (
                <AddBankModal
                    open={true}
                    onClose={() => {
                        setShowAddBankModal(false);
                    }}
                />
            )}


            {showAddCryptoWalletModal && (
                <AddCryptoWalletModal
                    open={true}
                    onClose={() => {
                        setShowAddCryptoWalletModal(false);
                    }}
                />
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={2000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: isMobile ? 'center' : 'right'
                }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        maxWidth: isMobile ? '100%' : '400px'
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            <FeedbackModal
                open={isFeedbackModalOpen}
                onClose={() => setIsFeedbackModalOpen(false)}
                onContinue={() => {
                    onClose();
                }}
            />
            <DeleteConfirmationModal 
                open={Boolean(bankToDelete)}
                accountEnding={bankToDelete?.slice(-4)}
                isDeleting={Boolean(deletingBankId)}
                onCancel={() => setBankToDelete(null)}
                onConfirm={confirmDeleteBank}
            />
        </>
    );
};

export default WithdrawModal;